'use client';

import { useEffect } from 'react';
import { captureClientException } from '@instamenu/posthog/client';
import { Emoji, Text } from '@instamenu/ui-system-next';
import { Button } from '@instamenu/ui-system-next';
import { CenteredBox } from '@instamenu/ui-system-next';
import { useLocale } from '@instamenu/context-i18n';

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { i18n } = useLocale();
  const errors = [
    {
      emoji: '🤓',
      title: i18n.t('appErrors.newAppVersionTitle'),
      btnLabel: i18n.t('appErrors.reloadLabel'),
      message: i18n.t('appErrors.newAppVersionDescription'),
      fn: () => window.location.reload(),
      test: (e: Error) => /Loading chunk \d+ failed/i.test(e.message),
    },
    {
      emoji: '🤕',
      title: i18n.t('appErrors.unexpectedErrorTitle'),
      message: i18n.t('appErrors.unexpectedErrorDescription'),
      btnLabel: i18n.t('appErrors.retryLabel'),
      fn: () => reset(),
      test: () => true,
    },
  ];

  useEffect(() => {
    captureClientException(error, {
      digest: error.digest,
      source: 'route-error',
    });
  }, [error]);

  const errorType = errors.find((candidate) => candidate.test(error)) ?? errors[errors.length - 1];
  if (!errorType) return null;

  return (
    <CenteredBox
      style={{ height: '100dvh', maxWidth: '40em', textAlign: 'center', gap: 16, margin: '0 auto' }}
    >
      <Text variant="h1">
        <Emoji emoji={errorType.emoji} ariaLabel="emoji" />
      </Text>
      <Text variant="h3">{errorType.title}</Text>
      <Text variant="body1">{errorType.message}</Text>
      <Button onClick={errorType.fn}>{errorType.btnLabel}</Button>
    </CenteredBox>
  );
}
